import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Smartphone, X, Share, WifiOff } from "lucide-react";

const KEY = "rp_install_dismissed";
let deferred = null;
if (typeof window !== "undefined") window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; window.dispatchEvent(new Event("rp-installable")); });

export function InstallPrompt() {
  const [installable, setInstallable] = useState(!!deferred);
  const [hidden, setHidden] = useState(() => localStorage.getItem(KEY) === "1");
  const standalone = typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone);
  const ios = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
  useEffect(() => { const f = () => setInstallable(true); window.addEventListener("rp-installable", f); window.addEventListener("appinstalled", () => setHidden(true)); return () => window.removeEventListener("rp-installable", f); }, []);
  if (standalone || hidden || (!installable && !ios)) return null;
  const dismiss = () => { localStorage.setItem(KEY, "1"); setHidden(true); };
  const install = async () => { if (!deferred) return; deferred.prompt(); const { outcome } = await deferred.userChoice; deferred = null; if (outcome === "accepted") setHidden(true); };
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 sm:p-4 flex items-start gap-3" data-testid="install-prompt">
      <Smartphone className="w-5 h-5 text-primary shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-semibold">Add RightPay to your home screen</div>
        <p className="text-xs text-muted-foreground mt-0.5">Open paystubs in one tap — your latest paystubs stay available even without a signal.</p>
        {ios && !installable && <p className="text-xs mt-1.5 flex items-center gap-1" data-testid="install-ios-hint">Tap <Share className="w-3.5 h-3.5 inline" /> Share in Safari, then <b>Add to Home Screen</b>.</p>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {installable && <Button size="sm" data-testid="install-btn" onClick={install}>Install</Button>}
        <Button size="icon" variant="ghost" className="h-8 w-8" data-testid="install-dismiss" onClick={dismiss}><X className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

export function OfflineBadge({ cached }) {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => { const on = () => setOnline(true), off = () => setOnline(false); window.addEventListener("online", on); window.addEventListener("offline", off); return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); }; }, []);
  if (online && !cached) return null;
  return <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 flex items-center gap-2" data-testid="offline-badge"><WifiOff className="w-3.5 h-3.5" /> {online ? "Showing your saved copy — reconnecting…" : "You're offline — showing paystubs saved on this device. PDFs you opened before are still available."}</div>;
}
